import { useState } from 'react';
import { jobSiteLine } from '../../lib/job-site.js';
import {
  AlertTriangle,
  CalendarOff,
  Check,
  GripVertical,
  Pause,
} from 'lucide-react';
import { cn } from '../../lib/cn.js';
import {
  absoluteHourLabel,
  BLOCK_HOURS,
  FOP_UDF,
  hoursToColumns,
  isSlotVisible,
  readCompletedAt,
  slotToViewColumn,
  todayLocalIso,
  useDispatchBoard,
  viewColumnToSlot,
  type Technician,
} from '../../lib/dispatch-board.js';
import { tradeLabel, tradeTint, type Trade } from '../../lib/trade.js';
import type { FieldPhase, SalesOrderSummary, SalesType } from '../../lib/api.js';
import { isWholeDayOff, offDuring, type Unavailable } from '../../lib/availability.js';

interface BoardGridProps {
  /** Active dispatch roster — drives the rows + skill-match warning. */
  /** Company sales types — colours + labels for the placed cards. */
  salesTypes: ReadonlyArray<SalesType>;
  techs: ReadonlyArray<Technician>;
  /** Why each tech is unavailable on the board's selected day (absent = working). */
  unavailable: ReadonlyMap<string, Unavailable>;
  /** Orders that have a local assignment, plus the resolved tech + start.
   * The board receives one entry per (tech, role) — leads get the
   * primary entry, helpers get a "ghost" entry so their row visibly
   * shows the same time block. Drag is lead-only. */
  placed: ReadonlyArray<{
    order: SalesOrderSummary;
    trade: Trade;
    techId: string;
    startHour: number;
    duration: number;
    role: 'lead' | 'helper';
    /** Set when this block is an EXTRA visit (lib/visits.ts) rather than the
     * order's own schedule — a drag then moves the visit, not the order. */
    visitId?: string;
  }>;
  /** Trade the user is currently dragging (controls drop-zone tone). */
  draggingTrade: Trade | null;
  /**
   * Place a card on the timeline. Called for both new drops from the
   * queue rail AND for moves of already-placed cards. `currentSlot` is
   * present only when moving — the route uses it to preserve the
   * existing duration instead of resetting to the default.
   */
  onAssign: (orderId: number, techId: string, startHour: number, currentSlot?: number, visitId?: string) => void;
}

/**
 * Tech rows × hour columns. Drop zones highlight green for compatible
 * trades, amber for skill mismatch, but accept both — final assignment
 * authority belongs to the dispatcher, not the board.
 *
 * Placed blocks use CSS `grid-column: span N` so multi-hour jobs render
 * correctly without manual width math.
 */
export function BoardGrid({ salesTypes, techs, unavailable, placed, draggingTrade, onAssign }: BoardGridProps) {
  const selectedOrderId = useDispatchBoard((s) => s.selectedOrderId);
  const select = useDispatchBoard((s) => s.select);
  const viewStartHour = useDispatchBoard((s) => s.viewStartHour);
  const viewHours = useDispatchBoard((s) => s.viewHours);
  // An extra visit only shows the job's live phase on today's board — the
  // order has ONE phase, and a pulsing block on next Tuesday would lie.
  const isToday = useDispatchBoard((s) => s.selectedDate) === todayLocalIso();

  if (techs.length === 0) {
    return (
      <div className="p-3 min-h-[320px] flex items-center justify-center">
        <div className="text-sm text-ink-muted text-center max-w-sm">
          No active techs. Add one in <span className="font-mono">/admin → Skills matrix</span>{' '}
          and they'll show up here.
        </div>
      </div>
    );
  }

  // Count placed cards whose stored slot falls outside the current view —
  // we render a small banner so the dispatcher knows those exist but are
  // currently hidden by the view window (a tighter workday picker).
  const hidden = placed.filter((p) => !isSlotVisible(p.startHour, viewStartHour, viewHours)).length;

  return (
    <div className="p-3 min-h-[320px] overflow-x-auto">
      <HeaderRow viewStartHour={viewStartHour} viewHours={viewHours} />
      {techs.map((tech) => (
        <TechRow
          key={tech.id}
          salesTypes={salesTypes}
          tech={tech}
          unavailable={unavailable.get(tech.id) ?? null}
          placed={placed.filter((p) => p.techId === tech.id)}
          draggingTrade={draggingTrade}
          selectedOrderId={selectedOrderId}
          isToday={isToday}
          viewStartHour={viewStartHour}
          viewHours={viewHours}
          onAssign={onAssign}
          onSelect={select}
        />
      ))}
      {hidden > 0 && (
        <div
          className="mt-2 rounded-md border px-2 py-1 text-[10.5px] text-ink-muted"
          style={{
            borderColor: 'var(--color-border-tertiary)',
            backgroundColor: 'var(--color-background-secondary)',
          }}
        >
          {hidden} placed {hidden === 1 ? 'job is' : 'jobs are'} outside the current workday view.
          Widen the workday picker to see {hidden === 1 ? 'it' : 'them'}.
        </div>
      )}
    </div>
  );
}

function HeaderRow({ viewStartHour, viewHours }: { viewStartHour: number; viewHours: number }) {
  const cols = hoursToColumns(viewHours);
  return (
    <div
      className="grid gap-1 py-1 text-[10px] text-ink-subtle"
      style={{ gridTemplateColumns: `120px repeat(${cols}, minmax(30px, 1fr))` }}
    >
      <div className="pl-1">Technician</div>
      {/* One label per HOUR, spanning its two half-hour columns; a stray
         half column at the end (odd view) just gets a narrow label. */}
      {Array.from({ length: Math.ceil(cols / 2) }).map((_, h) => (
        <div
          key={h}
          className="text-left pl-1 border-l"
          style={{
            gridColumn: `span ${Math.min(2, cols - h * 2)}`,
            borderColor: 'var(--color-border-tertiary)',
          }}
        >
          {absoluteHourLabel(viewStartHour + h)}
        </div>
      ))}
    </div>
  );
}

function TechRow({
  salesTypes,
  tech,
  unavailable,
  placed,
  draggingTrade,
  selectedOrderId,
  isToday,
  viewStartHour,
  viewHours,
  onAssign,
  onSelect,
}: {
  isToday: boolean;
  salesTypes: ReadonlyArray<SalesType>;
  tech: Technician;
  unavailable: Unavailable | null;
  placed: BoardGridProps['placed'];
  draggingTrade: Trade | null;
  selectedOrderId: number | null;
  viewStartHour: number;
  viewHours: number;
  onAssign: BoardGridProps['onAssign'];
  onSelect: (orderId: number | null) => void;
}) {
  // Compute overlap set for this tech — any two placements whose hour
  // ranges intersect get flagged. Used by both Slot (red drop tone on
  // hours that would collide) and PlacedBlock (border + icon on existing
  // overlap). One-time O(n²) per render, fine for a typical tech-day's
  // handful of placements.
  const overlapIds = computeOverlaps(placed);
  // A part-day entry (a morning off) shades only its own hours, not the row.
  const wholeDayOff = unavailable !== null && isWholeDayOff(unavailable);
  return (
    <div
      className={cn('grid gap-1 py-1 border-t items-stretch', wholeDayOff && 'opacity-70')}
      style={{
        gridTemplateColumns: `120px repeat(${hoursToColumns(viewHours)}, minmax(30px, 1fr))`,
        borderColor: 'var(--color-border-tertiary)',
        // Hatched background says "not working today" without hiding
        // anything already placed there (those still need attention).
        ...(wholeDayOff
          ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, var(--color-background-tertiary) 6px 8px)' }
          : {}),
      }}
      title={unavailable ? `${tech.name}: ${unavailable.label}` : undefined}
    >
      <TechCell tech={tech} salesTypes={salesTypes} unavailable={unavailable} />
      {/* Slot cells (one per 30-minute block) provide the drop targets —
         placed blocks render on top. `col` is the visible column; we
         translate to an absolute start hour via `viewColumnToSlot`. */}
      {Array.from({ length: hoursToColumns(viewHours) }).map((_, col) => {
        const targetSlot = viewColumnToSlot(col, viewStartHour);
        // Slot is occupied if any existing placement covers this slot.
        // Drop tone flips amber/red so dispatcher sees the collision
        // before releasing. Compatible-trade green still wins when free.
        const occupied = placed.some(
          (p) => targetSlot >= p.startHour && targetSlot < p.startHour + p.duration,
        );
        // Unavailable day = amber warn, same treatment as a skill mismatch:
        // allowed, but the dispatcher sees it before releasing.
        const slotOff = offDuring(unavailable, targetSlot, BLOCK_HOURS);
        const baseTone = draggingTrade
          ? (tech.trades.includes(draggingTrade as never) && !slotOff ? 'ok' : 'warn')
          : null;
        const tone = occupied && draggingTrade ? 'collide' : baseTone;
        return (
          <Slot
            key={`s-${col}`}
            techId={tech.id}
            col={col}
            viewStartHour={viewStartHour}
            dropTone={tone}
            shaded={slotOff && !wholeDayOff}
            onDrop={(orderId, sourceSlot, visitId) =>
              onAssign(
                orderId,
                tech.id,
                viewColumnToSlot(col, viewStartHour),
                sourceSlot === null ? undefined : sourceSlot,
                visitId ?? undefined,
              )
            }
          />
        );
      })}
      {/* Placed blocks — render only when their stored slot intersects the
         current view. Off-view blocks are surfaced in the BoardGrid footer
         banner instead of rendering at an invalid column. */}
      {placed
        .filter((p) => isSlotVisible(p.startHour, viewStartHour, viewHours))
        .map((p) => (
          <PlacedBlock
            key={`${p.order.spireId}:${p.visitId ?? 'first'}`}
            salesTypes={salesTypes}
            orderId={p.order.spireId}
            visitId={p.visitId ?? null}
            // gridColumn uses 1-based numbering; column 1 = tech cell, so
            // a placed block at view-column c renders at `2 + c`.
            viewCol={slotToViewColumn(p.startHour, viewStartHour)}
            duration={hoursToColumns(p.duration)}
            overlapping={overlapIds.has(p.order.spireId)}
            role={p.role}
            // Clamp the visible span so a 4h block at view-column 6 in a
            // 7-wide view doesn't blow past the grid. Hidden hours still
            // belong to the assignment.
            visibleDuration={Math.max(
              1,
              Math.min(hoursToColumns(p.duration), hoursToColumns(viewHours) - slotToViewColumn(p.startHour, viewStartHour)),
            )}
            sourceSlot={p.startHour}
            trade={p.trade}
            title={p.order.customer.name}
            // Job site first — on a narrow card it's what the dispatcher
            // routes by; the order # stays reachable via hover + side panel.
            subtitle={[p.visitId ? 'Extra visit' : null, jobSiteLine(p.order), p.order.orderNo, `$${p.order.total}`]
              .filter(Boolean)
              .join(' · ')}
            isQuote={p.order.type === 'quote'}
            selected={selectedOrderId === p.order.spireId}
            completed={readCompletedAt(p.order.udf ?? {}) !== null}
            // Mismatch when the order's trade isn't in the assigned tech's
            // skills. The dispatcher chose this on purpose (the drop was
            // accepted with a warn tone) — we keep it visible after the
            // drop so it's not forgotten.
            skillMismatch={!tech.trades.includes(p.trade as never)}
            // Placed on a day the tech is off (booked after the fact, or a
            // holiday added later). Flagged, never moved — the dispatcher
            // decides whether to reassign.
            techOff={offDuring(unavailable, p.startHour, p.duration) ? (unavailable?.label ?? null) : null}
            phase={p.visitId && !isToday ? null : readPhase(p.order.udf?.[FOP_UDF.phase])}
            onClick={() =>
              onSelect(selectedOrderId === p.order.spireId ? null : p.order.spireId)
            }
          />
        ))}
    </div>
  );
}

function TechCell({
  tech,
  salesTypes,
  unavailable,
}: {
  tech: Technician;
  salesTypes: ReadonlyArray<SalesType>;
  unavailable: Unavailable | null;
}) {
  const swatch = AVATAR_SWATCH[tech.avatar];
  return (
    <div className="flex items-center gap-2 px-1">
      <span
        aria-hidden
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold"
        style={{ backgroundColor: swatch.bg, color: swatch.fg }}
      >
        {tech.initials}
      </span>
      <div className="min-w-0 leading-tight">
        <div className="text-xs font-medium text-ink truncate">{tech.name}</div>
        <div className="text-[10px] text-ink-muted truncate">
          {!tech.active ? (
            <span className="font-medium text-amber-700">Inactive — reassign</span>
          ) : unavailable ? (
            <span className="font-medium text-amber-700">{unavailable.label}</span>
          ) : (
            tech.trades.map((t) => tradeLabel(salesTypes, t)).join(' · ')
          )}
        </div>
      </div>
    </div>
  );
}

const AVATAR_SWATCH: Record<Technician['avatar'], { bg: string; fg: string }> = {
  install: { bg: '#CECBF6', fg: '#26215C' },
  service: { bg: '#B5D4F4', fg: '#042C53' },
  pm: { bg: '#9FE1CB', fg: '#04342C' },
  emergency: { bg: '#F7C1C1', fg: '#501313' },
  sales: { bg: '#FAC775', fg: '#412402' },
};

function Slot({
  techId,
  col,
  viewStartHour,
  dropTone,
  shaded,
  onDrop,
}: {
  techId: string;
  /** Inside a part-day time-off window — hatched so the booked hours read at a glance. */
  shaded: boolean;
  /** Visible column index (0..viewHours-1). */
  col: number;
  viewStartHour: number;
  /** `ok` = compatible drop zone (trade matches); `warn` = skill mismatch
   * but still allowed; `collide` = an existing placement already occupies
   * this hour on this tech. `null` = no drag in progress. */
  dropTone: 'ok' | 'warn' | 'collide' | null;
  /** `sourceSlot` is the FOP_START of a placed card being moved; null when
   * the drag originated from the queue rail. The route uses this to
   * preserve duration on moves vs. picking the default on first drop.
   * `visitId` is set when the dragged block was an extra visit. */
  onDrop: (orderId: number, sourceSlot: number | null, visitId: string | null) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      data-tech={techId}
      data-col={col}
      data-hour={viewColumnToSlot(col, viewStartHour)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData('text/plain');
        const orderId = Number(raw);
        // Source slot is set by PlacedBlock on dragstart so the route can
        // preserve the existing duration on a move; queue-rail drags omit
        // it (fall back to the default duration in handleAssign).
        const slotRaw = e.dataTransfer.getData('application/x-tinsmith-source-slot');
        const sourceSlot = slotRaw !== '' ? Number(slotRaw) : Number.NaN;
        const visitId = e.dataTransfer.getData('application/x-tinsmith-visit');
        if (Number.isFinite(orderId)) {
          onDrop(orderId, Number.isFinite(sourceSlot) ? sourceSlot : null, visitId !== '' ? visitId : null);
        }
        setOver(false);
      }}
      // A faint left edge on each :00 column so the hour boundaries read
      // through the half-hour grid; :30 columns stay borderless.
      className={cn('rounded-md min-h-[44px] transition-colors', col % 2 === 0 && 'border-l')}
      style={
        dropTone === 'collide'
          ? {
              backgroundColor: 'rgba(239, 68, 68, 0.18)',
              boxShadow: over
                ? 'inset 0 0 0 2px rgba(239, 68, 68, 0.6)'
                : 'inset 0 0 0 1px rgba(239, 68, 68, 0.35)',
            }
          : dropTone === 'ok'
            ? {
                backgroundColor: 'var(--feedback-success-bg)',
                boxShadow: over ? 'inset 0 0 0 1px var(--feedback-success-border)' : undefined,
              }
            : dropTone === 'warn'
              ? {
                  backgroundColor: 'var(--feedback-warning-bg)',
                  boxShadow: over ? 'inset 0 0 0 1px var(--feedback-warning-border)' : undefined,
                }
              : shaded
                ? {
                    backgroundColor: 'var(--color-background-secondary)',
                    backgroundImage:
                      'repeating-linear-gradient(135deg, transparent 0 6px, var(--color-background-tertiary) 6px 8px)',
                  }
                : { backgroundColor: 'var(--color-background-secondary)' }
      }
    />
  );
}

function PlacedBlock({
  salesTypes,
  orderId,
  visitId,
  viewCol,
  visibleDuration,
  duration,
  sourceSlot,
  trade,
  title,
  subtitle,
  isQuote,
  selected,
  completed,
  skillMismatch,
  techOff,
  overlapping,
  role,
  phase,
  onClick,
}: {
  orderId: number;
  /** Non-null when this block is an extra visit — travels in the drag payload. */
  visitId: string | null;
  /** Visible column position (0..viewHours-1). */
  viewCol: number;
  /** Total scheduled duration in hours (unclamped — used for the drag payload). */
  duration: number;
  /** Hours actually visible inside the view window — clamped at the right edge. */
  visibleDuration: number;
  /** The block's stored FOP_START slot — travels in the drag payload so the
   * drop target can tell "move" apart from "new from queue". */
  sourceSlot: number;
  salesTypes: ReadonlyArray<SalesType>;
  trade: Trade;
  title: string;
  subtitle: string;
  isQuote: boolean;
  selected: boolean;
  completed: boolean;
  /** True when the assigned tech doesn't carry this order's trade in their
   * skills list. The dispatcher acknowledged it on the drop (the slot
   * tinted amber); we keep showing the indicator so the mismatch isn't
   * forgotten an hour later. */
  skillMismatch: boolean;
  /** Why the assigned tech is off on this day (holiday / time off / not a
   * working day), or null when they're working. Shown as a flag so a job
   * that landed on a day off — usually because the time off was booked
   * after the job was scheduled — is visible without being auto-moved. */
  techOff: string | null;
  /** True when this card's hour range intersects another placement on the
   * SAME tech — i.e. the dispatcher double-booked them. Renders a red
   * border + AlertTriangle so the conflict isn't silently absorbed. */
  overlapping: boolean;
  /** `lead` = the primary assignment (drives phase + clock-out + draggable).
   * `helper` = a ghost on a helper's row (display-only — drag is locked
   * because moving the lead is the only way to retime the visit). */
  role: 'lead' | 'helper';
  /** Current field phase from FOP_PHASE, if the tech has started the
   * job. Drives the in-progress visual cues (pulse, pause icon). */
  phase: FieldPhase | null;
  onClick: () => void;
}) {
  const cfg = tradeTint(salesTypes, trade);
  const inProgress =
    !completed && phase !== null && phase !== 'scheduled' && phase !== 'signed_off';
  const isPaused = phase === 'paused';
  // Completed jobs are still draggable — the route's assignmentUdfPatch
  // clears FOP_COMPLETED_AT on re-drop so a rescheduled completed visit
  // re-opens as "in progress". A tech actively working a job (phase set
  // to non-terminal) is the one drag we lock out — we shouldn't be able
  // to move a card out from under a tech mid-visit.
  // Helper ghosts are display-only — moving the time block has to happen
  // on the lead's card so we don't multiply the truth (drag from a
  // helper row would otherwise look like it's reassigning to that tech).
  const draggable = !inProgress && role === 'lead';
  const isHelperGhost = role === 'helper';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData('text/plain', String(orderId));
        // The slot identifies this as a move (route preserves duration);
        // queue cards don't set this so the route falls back to the
        // default duration on first drop.
        e.dataTransfer.setData('application/x-tinsmith-source-slot', String(sourceSlot));
        if (visitId) e.dataTransfer.setData('application/x-tinsmith-visit', visitId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      aria-grabbed={draggable ? 'false' : undefined}
      title={
        isHelperGhost
          ? 'Helping the lead on this visit — re-time via the lead\'s card'
          : draggable
            ? completed
              ? 'Drag to reschedule (clears the Done flag)'
              : techOff
                ? `Tech is booked off (${techOff}) — drag to another tech or time`
                : 'Drag to move within this day'
            : 'Visit in progress — pause or wait for clock-out before moving'
      }
      className={cn(
        'group relative rounded-md px-2 py-1.5 text-left text-[11px] leading-tight overflow-hidden transition-shadow',
        'border',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        selected && 'ring-2 ring-offset-1',
        // Completed cards lose color saturation + get a strike-through on the
        // title. Still draggable so the dispatcher can re-schedule history
        // into a new visit without unscheduling first.
        completed && 'opacity-65',
        // Paused jobs get desaturated so the dispatcher sees "tech is on
        // break" without needing to read the phase label.
        isPaused && 'opacity-80',
        // Helper ghosts are dimmed so the lead's primary card is the
        // visual anchor. Same trade color so the schedule still reads as
        // one job across rows.
        isHelperGhost && 'opacity-70',
      )}
      style={{
        // `2 +` because column 1 is the tech-name column.
        gridColumn: `${2 + viewCol} / span ${visibleDuration}`,
        backgroundColor: cfg.bg,
        color: cfg.fg,
        // Overlap wins over phase + role styling — the red border is the
        // loud "fix this!" signal and shouldn't be visually outranked.
        borderColor: overlapping ? '#DC2626' : cfg.border,
        borderWidth: overlapping ? 2 : inProgress ? 2 : 1,
        // Dashed border on helper ghosts signals "this is a reference,
        // not the primary booking". Solid borders stay for leads.
        borderStyle: isHelperGhost && !overlapping ? 'dashed' : 'solid',
      }}
    >
      <div className="flex items-center gap-1">
        {draggable && (
          <GripVertical
            className="h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100"
            aria-hidden
          />
        )}
        {completed && <Check className="h-3 w-3 shrink-0" aria-label="Completed" />}
        {isPaused && <Pause className="h-3 w-3 shrink-0" aria-label="Paused" />}
        {inProgress && !isPaused && (
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full animate-pulse shrink-0"
            style={{ backgroundColor: 'currentColor' }}
          />
        )}
        {overlapping && (
          <AlertTriangle
            className="h-3 w-3 shrink-0"
            style={{ color: '#DC2626' }}
            aria-label="Overlaps with another job on this tech"
          />
        )}
        {skillMismatch && !overlapping && (
          <AlertTriangle
            className="h-3 w-3 shrink-0"
            style={{ color: 'var(--feedback-warning-fg)' }}
            aria-label="Tech's skills don't include this trade"
          />
        )}
        {techOff && !overlapping && !completed && (
          <CalendarOff
            className="h-3 w-3 shrink-0"
            style={{ color: 'var(--feedback-warning-fg)' }}
            aria-label={`Tech is booked off: ${techOff}`}
          />
        )}
        <span className={cn('font-medium truncate', completed && 'line-through opacity-90')}>
          {title}
        </span>
        {isHelperGhost && (
          <span
            className="inline-block rounded-full text-[8.5px] font-semibold px-1 py-0.5 shrink-0 leading-none"
            style={{ backgroundColor: 'rgba(255,255,255,0.35)', color: 'currentColor' }}
            aria-label="Helper on this job"
          >
            Helper
          </span>
        )}
        {isQuote && (
          <span
            className="inline-block rounded-full text-[8.5px] font-semibold px-1 py-0.5 shrink-0 leading-none"
            style={{
              backgroundColor: 'var(--pill-sales-bg)',
              color: 'var(--pill-sales-fg)',
            }}
            aria-label="Quote (not yet a work order)"
          >
            Quote
          </span>
        )}
      </div>
      <div className="opacity-85 truncate text-[10px]">{subtitle}</div>
      {visibleDuration < duration && (
        <div className="absolute right-0 top-0 bottom-0 w-1.5" style={{ background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.18))' }} aria-hidden />
      )}
    </div>
  );
}

function readPhase(value: string | undefined): FieldPhase | null {
  if (!value) return null;
  switch (value) {
    case 'scheduled':
    case 'traveling':
    case 'on_site':
    case 'work_in_progress':
    case 'paused':
    case 'work_completed':
    case 'signed_off':
      return value;
    default:
      return null;
  }
}

/**
 * Compute the set of spireIds that overlap with at least one other card
 * on the same tech. Two placements overlap when their hour ranges
 * intersect — i.e. `aStart < bStart + bDuration && bStart < aStart + aDuration`.
 * O(n²) where n is the per-tech card count; for a typical 5–10 placements
 * per tech the cost is negligible vs the React commit anyway.
 */
function computeOverlaps(
  placed: ReadonlyArray<{ order: { spireId: number }; startHour: number; duration: number }>,
): Set<number> {
  const out = new Set<number>();
  for (let i = 0; i < placed.length; i++) {
    const a = placed[i]!;
    for (let j = i + 1; j < placed.length; j++) {
      const b = placed[j]!;
      const overlap =
        a.startHour < b.startHour + b.duration && b.startHour < a.startHour + a.duration;
      if (overlap) {
        out.add(a.order.spireId);
        out.add(b.order.spireId);
      }
    }
  }
  return out;
}

